/**
 * The page an employee reaches from the invitation email.
 *
 * ── The only unauthenticated page in the application besides /login ───────────────────────
 *
 * There is no session here. The person opening it has been hired but may not have signed in
 * once, and requiring SSO first would make the invitation useless for exactly the people it
 * exists for. What stands in for a session is the token in the URL: every call this page makes
 * carries it, and the server resolves WHICH employee from the token's own record rather than
 * from anything the page claims.
 *
 * That is why nothing here takes an employee email as an argument. The page never learns an
 * identifier it could substitute for somebody else's, so "open another employee's profile" is
 * not a request that can be formed from this screen.
 *
 * ── What the employee may change ──────────────────────────────────────────────────────────
 *
 * The sections come from config/profileFieldOwnership.ts, which is the single statement of who
 * owns what. Employment details render read-only at the top — deliberately shown rather than
 * hidden, because seeing what HR recorded is how somebody notices their designation or joining
 * date is wrong, and because a form that asks for your life history while telling you nothing
 * about itself reads as a phishing page.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  EmployeeSelfServiceProfile,
  ProfileInvitationContext,
  Qualification,
  PreviousEmployment,
  FamilyMember,
  Nomination,
} from '../../types/employeeProfile';
import { EmployeeProfileService } from '../../services/employeeProfileService';
import { sameIdentity } from '../../services/identity';
import {
  groupIssues,
  validateEmployeeProfile,
} from '../../services/employeeProfileValidation';
import { PROFILE_SECTIONS } from '../../config/profileFieldOwnership';
import DocumentUploads from '../common/DocumentUploads';
import { withPendingApplied } from '../../services/profileChangeSet';
import { ChangeRequestStatus } from '../../types/employeeProfile';
import './completeProfile.css';

/** A stable row id. crypto.randomUUID is unavailable on older Safari, hence the fallback. */
const rowId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const Field: React.FC<{
  label: string;
  value?: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
}> = ({ label, value, onChange, type = 'text', placeholder, required, maxLength }) => (
  <label className="cp-field">
    <span className="cp-field__label">
      {label}
      {required && <span className="cp-field__req"> *</span>}
    </span>
    <input
      type={type}
      value={value || ''}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const Select: React.FC<{
  label: string;
  value?: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, options, onChange, required }) => (
  <label className="cp-field">
    <span className="cp-field__label">
      {label}
      {required && <span className="cp-field__req"> *</span>}
    </span>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select…</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </label>
);

const Section: React.FC<{
  title: string;
  hint?: string;
  issues?: string[];
  children: React.ReactNode;
}> = ({ title, hint, issues, children }) => (
  <section className="cp-section">
    <h2 className="cp-section__title">{title}</h2>
    {hint && <p className="cp-section__hint">{hint}</p>}
    {issues && issues.length > 0 && (
      <ul className="cp-issues">
        {issues.map((issue) => (
          <li key={issue}>{issue}</li>
        ))}
      </ul>
    )}
    <div className="cp-grid">{children}</div>
  </section>
);

const GENDERS = ['Female', 'Male', 'Other', 'Prefer not to say'];
const MARITAL = ['Single', 'Married', 'Divorced', 'Widowed'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const RELATIONSHIPS = ['Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Other'];

const CompleteProfilePage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<ProfileInvitationContext | null>(null);
  const [profile, setProfile] = useState<Partial<EmployeeSelfServiceProfile>>({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [savedAt, setSavedAt] = useState('');

  useEffect(() => {
    let cancelled = false;
    EmployeeProfileService.openInvitation(token)
      .then((loaded) => {
        if (cancelled) return;
        setContext(loaded);
        /*
         * The form opens on approved values WITH the employee's own pending edits laid over them.
         *
         * Showing the approved value alone would look as though their submission had been thrown
         * away, so they would type it again and raise a duplicate request for HR to decide twice.
         * A REJECTED change deliberately does not overlay — that field falls back to the last
         * good value, and the rejection note below says why.
         */
        setProfile(loaded.profile ? withPendingApplied(loaded.profile) : {});
      })
      .catch((err: any) => !cancelled && setError(err?.message || 'This link could not be opened.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  /*
   * Issues are computed on every keystroke but only DISPLAYED after a submit attempt.
   *
   * Validating as you type turns a long form into a wall of red before anybody has finished a
   * field. Validating only on submit means scrolling to hunt for what went wrong. So: compute
   * always, reveal on the first submit, and from then on the messages update live as they are
   * fixed — which is the point at which "required" is useful information rather than nagging.
   */
  /** Changes HR turned down, with the reason. The only thing here needing the employee to act. */
  const rejected = useMemo(
    () =>
      (context?.profile?.pendingChanges || []).filter(
        (c) =>
          c.status === ChangeRequestStatus.REJECTED ||
          c.status === ChangeRequestStatus.CORRECTION_REQUESTED
      ),
    [context]
  );

  /** Still with HR. Shown so the employee knows their submission landed. */
  const awaiting = useMemo(
    () =>
      (context?.profile?.pendingChanges || []).filter(
        (c) => c.status === ChangeRequestStatus.PENDING
      ).length,
    [context]
  );

  const issues = useMemo(() => validateEmployeeProfile(profile, 'submit'), [profile]);
  const bySection = useMemo(() => groupIssues(issues), [issues]);
  const sectionIssues = (key: string) => (showIssues ? bySection[key] : undefined);

  const patch = useCallback(
    <K extends keyof EmployeeSelfServiceProfile>(
      key: K,
      value: EmployeeSelfServiceProfile[K]
    ) => setProfile((prev) => ({ ...prev, [key]: value })),
    []
  );

  const patchSection = useCallback((key: string, partial: Record<string, any>) => {
    setProfile((prev) => ({ ...prev, [key]: { ...((prev as any)[key] || {}), ...partial } }));
  }, []);

  const saveDraft = async () => {
    setSaving(true);
    setError('');
    try {
      await EmployeeProfileService.saveDraft(token, profile);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err: any) {
      setError(err?.message || 'Your progress could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setShowIssues(true);
    if (issues.length > 0) {
      setError(`Please fix ${issues.length} item${issues.length === 1 ? '' : 's'} before submitting.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setError('');
    try {
      await EmployeeProfileService.submit(token, profile);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err?.message || 'Your profile could not be submitted.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="cp cp--message">Opening your profile…</div>;

  if (error && !context) {
    return (
      <div className="cp cp--message">
        <h1>This link cannot be opened</h1>
        <p>{error}</p>
        <p className="cp-muted">
          Invitation links expire. Ask HR to send you a new one — your details are safe and
          nothing you entered before has been lost.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="cp cp--message cp--success">
        <h1>Your profile information has been submitted successfully.</h1>
        <p>
          Thank you, {context?.employeeName}. HR will review what you entered and approve it.
        </p>
        <p className="cp-muted">
          Your details become part of your record once HR approves them. If anything needs
          correcting they will tell you, and you can submit it again.
        </p>
      </div>
    );
  }

  const personal = profile.personal || {};
  const permanent = profile.address?.permanent || {};
  const present = profile.address?.present || {};
  const emergency = profile.emergencyContact || {};
  const ids = profile.identifications || {};
  const accounts = profile.accounts || {};
  const section = (key: string) => PROFILE_SECTIONS.find((s) => s.key === key);

  /**
   * The employee's name — or nothing, when the "name" on their record is really their email.
   *
   * UserProfiles.name is free text typed or imported by HR, and for a row created from a Cognito
   * account it is often the address itself. Printed into "{name} · {email}" that renders the same
   * address twice with a dot between it, which is the whole of the bug this guards.
   *
   * Compared with sameIdentity rather than `!==`, and that is the fix rather than a tidy-up: a
   * strict comparison only catches a name that matches the address byte for byte, so
   * "Bhavana.Kella@…" against "bhavana.kella@…", or an imported row carrying a trailing space,
   * walked straight past it and printed the duplicate anyway — indistinguishable on screen from
   * the case that was caught. sameIdentity trims, folds case and collapses the alias domains, so
   * every spelling of one address counts as one address.
   *
   * Derived ONCE here rather than checked at the point of render, so a second place that wants
   * the name cannot reintroduce the duplicate by forgetting the comparison.
   */
  const shownName =
    context?.employeeName && !sameIdentity(context.employeeName, context.employeeEmail)
      ? context.employeeName
      : '';

  return (
    /*
     * Two elements, not one: .cp paints the ground and fills the window, .cp__inner sets how
     * wide the form gets. Putting both on one element is what made this page render as a narrow
     * strip on a laptop — see the header of completeProfile.css.
     */
    <div className="cp">
      <div className="cp__inner">
        <header className="cp-head">
          {/*
            * The brand, on a page that sits outside the application shell.
            *
            * This is the first thing a new hire ever sees of the product, reached from a link in
            * an email, with no sidebar and no header to say whose site it is. The mark carries
            * alt="" because the wordmark beside it already says the name — announcing it twice
            * is what a screen reader would otherwise do.
            */}
          <div className="cp-brand">
            <img src="/ci-logo.png" alt="" className="cp-brand__mark" />
            <span className="cp-brand__name">Creyente One</span>
          </div>

          <h1>Complete your employee profile</h1>

          {/*
            * Name and address, or just the address — never the address twice. `shownName` is
            * empty both when there is no name and when the "name" is the address in another
            * spelling; see where it is derived for why that distinction cannot live here.
            */}
          <p>{shownName ? `${shownName} · ${context?.employeeEmail}` : context?.employeeEmail}</p>
        </header>

        {error && <div className="cp-banner cp-banner--error">{error}</div>}

        {/*
          * What HR has done since the employee last looked.
          *
          * Rejections first and with their reason, because that is the only thing on this page the
          * employee has to ACT on — a rejection with no reason visible leaves them resubmitting the
          * same value and nobody any wiser.
          */}
        {rejected.length > 0 && (
          <div className="cp-banner cp-banner--error">
            <strong>HR asked for changes to {rejected.length} item{rejected.length === 1 ? '' : 's'}:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {rejected.map((change) => (
                <li key={change.id}>
                  <strong>{change.label}</strong> — {change.decisionReason || 'no reason given'}
                </li>
              ))}
            </ul>
          </div>
        )}
        {awaiting > 0 && (
          <div className="cp-banner cp-banner--ok">
            {awaiting} change{awaiting === 1 ? ' is' : 's are'} waiting for HR to approve. Your
            current details stay as they are until then.
          </div>
        )}
        {savedAt && !error && (
          <div className="cp-banner cp-banner--ok">Progress saved at {savedAt}.</div>
        )}

        {/* Read-only: HR owns every field here. See the file header for why it is shown at all. */}
        <Section title={section('employment')!.title} hint={section('employment')!.hint}>
          <div className="cp-readonly">
            <span>Employee ID</span>
            <strong>{context?.employeeId || '—'}</strong>
          </div>
          <div className="cp-readonly">
            <span>Designation</span>
            <strong>{context?.designation || '—'}</strong>
          </div>
          <div className="cp-readonly">
            <span>Department</span>
            <strong>{context?.department || '—'}</strong>
          </div>
          <div className="cp-readonly">
            <span>Date of joining</span>
            <strong>{context?.dateOfJoining || '—'}</strong>
          </div>
        </Section>

        <Section
          title={section('personal')!.title}
          hint={section('personal')!.hint}
          issues={sectionIssues('personal')}
        >
          <Field
            label="Date of birth"
            type="date"
            required
            value={personal.dateOfBirth}
            onChange={(v) => patchSection('personal', { dateOfBirth: v })}
          />
          <Select
            label="Gender"
            required
            options={GENDERS}
            value={personal.gender}
            onChange={(v) => patchSection('personal', { gender: v })}
          />
          <Select
            label="Blood group"
            options={BLOOD_GROUPS}
            value={personal.bloodGroup}
            onChange={(v) => patchSection('personal', { bloodGroup: v })}
          />
          <Field
            label="Mobile number"
            required
            placeholder="+910000000000"
            value={personal.mobileNumber}
            onChange={(v) => patchSection('personal', { mobileNumber: v })}
          />
          <Field
            label="Personal email"
            type="email"
            value={personal.personalEmail}
            onChange={(v) => patchSection('personal', { personalEmail: v })}
          />
          <Field
            label="Father's name"
            required
            value={personal.fathersName}
            onChange={(v) => patchSection('personal', { fathersName: v })}
          />
          <Field
            label="Mother's name"
            value={personal.mothersName}
            onChange={(v) => patchSection('personal', { mothersName: v })}
          />
          <Select
            label="Marital status"
            options={MARITAL}
            value={personal.maritalStatus}
            onChange={(v) => patchSection('personal', { maritalStatus: v })}
          />
          {String(personal.maritalStatus || '').toLowerCase() === 'married' && (
            <>
              <Field
                label="Spouse name"
                required
                value={personal.spouseName}
                onChange={(v) => patchSection('personal', { spouseName: v })}
              />
              <Field
                label="Marriage date"
                type="date"
                value={personal.marriageDate}
                onChange={(v) => patchSection('personal', { marriageDate: v })}
              />
            </>
          )}
          <Field
            label="Nationality"
            value={personal.nationality}
            onChange={(v) => patchSection('personal', { nationality: v })}
          />
        </Section>

        <Section
          title={section('address')!.title}
          hint={section('address')!.hint}
          issues={sectionIssues('address')}
        >
          <Field
            label="Permanent address"
            required
            value={permanent.line1}
            onChange={(v) => patch('address', { ...profile.address, permanent: { ...permanent, line1: v } })}
          />
          <Field
            label="Address line 2"
            value={permanent.line2}
            onChange={(v) => patch('address', { ...profile.address, permanent: { ...permanent, line2: v } })}
          />
          <Field
            label="City"
            required
            value={permanent.city}
            onChange={(v) => patch('address', { ...profile.address, permanent: { ...permanent, city: v } })}
          />
          <Field
            label="District"
            value={permanent.district}
            onChange={(v) => patch('address', { ...profile.address, permanent: { ...permanent, district: v } })}
          />
          <Field
            label="State"
            required
            value={permanent.state}
            onChange={(v) => patch('address', { ...profile.address, permanent: { ...permanent, state: v } })}
          />
          <Field
            label="Country"
            value={permanent.country}
            onChange={(v) => patch('address', { ...profile.address, permanent: { ...permanent, country: v } })}
          />
          <Field
            label="PIN code"
            required
            maxLength={6}
            value={permanent.pincode}
            onChange={(v) =>
              patch('address', {
                ...profile.address,
                permanent: { ...permanent, pincode: v.replace(/[^0-9]/g, '') },
              })
            }
          />
          <label className="cp-field cp-field--wide">
            <span className="cp-field__label">
              <input
                type="checkbox"
                checked={Boolean(profile.address?.presentSameAsPermanent)}
                onChange={(e) =>
                  patch('address', {
                    ...profile.address,
                    presentSameAsPermanent: e.target.checked,
                    // Copied on tick rather than aliased, so unticking leaves something to edit
                    // instead of blanking the fields the employee just confirmed.
                    present: e.target.checked ? { ...permanent } : present,
                  })
                }
              />{' '}
              My present address is the same as my permanent address
            </span>
          </label>
          {!profile.address?.presentSameAsPermanent && (
            <>
              <Field
                label="Present address"
                value={present.line1}
                onChange={(v) => patch('address', { ...profile.address, present: { ...present, line1: v } })}
              />
              <Field
                label="Present city"
                value={present.city}
                onChange={(v) => patch('address', { ...profile.address, present: { ...present, city: v } })}
              />
              <Field
                label="Present state"
                value={present.state}
                onChange={(v) => patch('address', { ...profile.address, present: { ...present, state: v } })}
              />
              <Field
                label="Present PIN code"
                maxLength={6}
                value={present.pincode}
                onChange={(v) =>
                  patch('address', {
                    ...profile.address,
                    present: { ...present, pincode: v.replace(/[^0-9]/g, '') },
                  })
                }
              />
            </>
          )}
        </Section>

        <Section
          title={section('emergencyContact')!.title}
          hint={section('emergencyContact')!.hint}
          issues={sectionIssues('emergencyContact')}
        >
          <Field
            label="Name"
            required
            value={emergency.name}
            onChange={(v) => patchSection('emergencyContact', { name: v })}
          />
          <Field
            label="Mobile number"
            required
            placeholder="+910000000000"
            value={emergency.mobileNumber}
            onChange={(v) => patchSection('emergencyContact', { mobileNumber: v })}
          />
          <Select
            label="Relationship"
            required
            options={RELATIONSHIPS}
            value={emergency.relationship}
            onChange={(v) => patchSection('emergencyContact', { relationship: v })}
          />
        </Section>

        <RepeatingSection
          title={section('qualifications')!.title}
          required
          hint={section('qualifications')!.hint}
          issues={sectionIssues('qualifications')}
          rows={profile.qualifications || []}
          addLabel="Add qualification"
          onAdd={() => patch('qualifications', [...(profile.qualifications || []), { id: rowId() }])}
          onRemove={(id) =>
            patch('qualifications', (profile.qualifications || []).filter((r) => r.id !== id))
          }
          onChange={(rows) => patch('qualifications', rows)}
          renderRow={(row: Qualification, update) => (
            <>
              <Field label="Qualification" required value={row.qualification} onChange={(v) => update({ qualification: v })} />
              <Field label="Institute" required value={row.institute} onChange={(v) => update({ institute: v })} />
              <Field label="From year" maxLength={4} value={row.fromYear} onChange={(v) => update({ fromYear: v.replace(/[^0-9]/g, '') })} />
              <Field label="To year" maxLength={4} value={row.toYear} onChange={(v) => update({ toYear: v.replace(/[^0-9]/g, '') })} />
              <Field label="Grade / percentage" value={row.grade} onChange={(v) => update({ grade: v })} />
              {/* Per ROW, so a degree certificate stays with the degree it belongs to. The row's
                  stable id is what makes that survive a row above it being deleted. */}
              <DocumentUploads
                scope={{ token }}
                section="qualifications"
                label="Certificate"
                required
                keys={row.documentKeys}
                onChange={(keys) => update({ documentKeys: keys })}
                disabled={saving}
              />
            </>
          )}
        />

        <RepeatingSection
          title={section('previousEmployment')!.title}
          hint={section('previousEmployment')!.hint}
          issues={sectionIssues('previousEmployment')}
          rows={profile.previousEmployment || []}
          addLabel="Add employer"
          onAdd={() =>
            patch('previousEmployment', [...(profile.previousEmployment || []), { id: rowId() }])
          }
          onRemove={(id) =>
            patch('previousEmployment', (profile.previousEmployment || []).filter((r) => r.id !== id))
          }
          onChange={(rows) => patch('previousEmployment', rows)}
          renderRow={(row: PreviousEmployment, update) => (
            <>
              <Field label="Company name" required value={row.companyName} onChange={(v) => update({ companyName: v })} />
              <Field label="Designation" value={row.designation} onChange={(v) => update({ designation: v })} />
              <Field label="From" type="date" value={row.fromDate} onChange={(v) => update({ fromDate: v })} />
              <Field label="To" type="date" value={row.toDate} onChange={(v) => update({ toDate: v })} />
              <Field label="Last drawn salary" value={row.lastDrawnSalary} onChange={(v) => update({ lastDrawnSalary: v })} />
              <Field label="Reason for leaving" value={row.reasonForLeaving} onChange={(v) => update({ reasonForLeaving: v })} />
              <DocumentUploads
                scope={{ token }}
                section="previousEmployment"
                label="Relieving or experience letter"
                keys={row.documentKeys}
                onChange={(keys) => update({ documentKeys: keys })}
                disabled={saving}
              />
            </>
          )}
        />

        <Section
          title={section('identifications')!.title}
          hint={section('identifications')!.hint}
          issues={sectionIssues('identifications')}
        >
          <Field
            label="Aadhaar number"
            required
            maxLength={12}
            placeholder="123456789012"
            value={ids.aadhaarNumber}
            onChange={(v) => patchSection('identifications', { aadhaarNumber: v.replace(/[^0-9]/g, '') })}
          />
          <Field
            label="PAN"
            required
            maxLength={10}
            placeholder="ABCDE1234F"
            value={ids.panNumber}
            onChange={(v) =>
              patchSection('identifications', {
                panNumber: v.replace(/[^A-Za-z0-9]/g, '').toUpperCase(),
              })
            }
          />
          <Field label="Passport number" value={ids.passportNumber} onChange={(v) => patchSection('identifications', { passportNumber: v })} />
          <Field label="Passport expiry" type="date" value={ids.passportExpiry} onChange={(v) => patchSection('identifications', { passportExpiry: v })} />
          <Field label="Driving licence" value={ids.drivingLicence} onChange={(v) => patchSection('identifications', { drivingLicence: v })} />
          {/*
            * The cards the numbers above were copied from.
            *
            * The section has been marked `acceptsDocuments` since it was written and there has
            * never been anywhere to attach one, so HR has held a twelve-digit Aadhaar typed by
            * the employee and no scan of the card to check it against.
            *
            * The keys are stored on the section, with the numbers, rather than in the profile's
            * top-level `documentKeys` — so a document is filed against the thing it evidences and
            * a reader can tell the Aadhaar scan from the cheque without opening either.
            */}
          <DocumentUploads
            scope={{ token }}
            section="identifications"
            label="Aadhaar card"
            required
            keys={ids.aadhaarDocumentKeys}
            onChange={(keys) => patchSection('identifications', { aadhaarDocumentKeys: keys })}
            disabled={saving}
          />
          <DocumentUploads
            scope={{ token }}
            section="identifications"
            label="PAN card"
            required
            keys={ids.panDocumentKeys}
            onChange={(keys) => patchSection('identifications', { panDocumentKeys: keys })}
            disabled={saving}
          />
          <DocumentUploads
            scope={{ token }}
            section="identifications"
            label="Other identity documents"
            hint="Passport or driving licence, if you have them."
            keys={ids.documentKeys}
            onChange={(keys) => patchSection('identifications', { documentKeys: keys })}
            disabled={saving}
          />
        </Section>

        <RepeatingSection
          title={section('family')!.title}
          issues={sectionIssues('family')}
          rows={profile.family || []}
          addLabel="Add family member"
          onAdd={() => patch('family', [...(profile.family || []), { id: rowId() }])}
          onRemove={(id) => patch('family', (profile.family || []).filter((r) => r.id !== id))}
          onChange={(rows) => patch('family', rows)}
          renderRow={(row: FamilyMember, update) => (
            <>
              <Field label="Name" required value={row.name} onChange={(v) => update({ name: v })} />
              <Field label="Relationship" required value={row.relationship} onChange={(v) => update({ relationship: v })} />
              <Field label="Date of birth" type="date" value={row.dateOfBirth} onChange={(v) => update({ dateOfBirth: v })} />
              <Field label="Occupation" value={row.occupation} onChange={(v) => update({ occupation: v })} />
            </>
          )}
        />

        <RepeatingSection
          title={section('nominations')!.title}
          hint={section('nominations')!.hint}
          issues={sectionIssues('nominations')}
          rows={profile.nominations || []}
          addLabel="Add nominee"
          onAdd={() => patch('nominations', [...(profile.nominations || []), { id: rowId() }])}
          onRemove={(id) => patch('nominations', (profile.nominations || []).filter((r) => r.id !== id))}
          onChange={(rows) => patch('nominations', rows)}
          renderRow={(row: Nomination, update) => (
            <>
              <Field label="Nominee name" required value={row.nomineeName} onChange={(v) => update({ nomineeName: v })} />
              <Field label="Relationship" required value={row.relationship} onChange={(v) => update({ relationship: v })} />
              <Field label="Date of birth" type="date" value={row.dateOfBirth} onChange={(v) => update({ dateOfBirth: v })} />
              <Field label="Share %" maxLength={3} value={row.sharePercentage} onChange={(v) => update({ sharePercentage: v.replace(/[^0-9]/g, '') })} />
              <Field label="Nomination for" placeholder="PF / Gratuity / Insurance" value={row.nominationFor} onChange={(v) => update({ nominationFor: v })} />
            </>
          )}
        />

        <Section
          title={section('accounts')!.title}
          hint={section('accounts')!.hint}
          issues={sectionIssues('accounts')}
        >
          <Field label="Bank name" value={accounts.bankName} onChange={(v) => patchSection('accounts', { bankName: v })} />
          <Field label="Branch" value={accounts.branch} onChange={(v) => patchSection('accounts', { branch: v })} />
          <Field label="Name as per bank" value={accounts.nameAsPerBank} onChange={(v) => patchSection('accounts', { nameAsPerBank: v })} />
          <Field
            label="Account number"
            required
            maxLength={18}
            value={accounts.accountNumber}
            onChange={(v) => patchSection('accounts', { accountNumber: v.replace(/[^0-9]/g, '') })}
          />
          <Field
            label="IFSC code"
            required
            maxLength={11}
            placeholder="ABCD0123456"
            value={accounts.ifscCode}
            onChange={(v) =>
              patchSection('accounts', { ifscCode: v.replace(/[^A-Za-z0-9]/g, '').toUpperCase() })
            }
          />
          <Field label="PF number" value={accounts.pfNumber} onChange={(v) => patchSection('accounts', { pfNumber: v })} />
          <Field
            label="UAN"
            maxLength={12}
            value={accounts.uanNumber}
            onChange={(v) => patchSection('accounts', { uanNumber: v.replace(/[^0-9]/g, '') })}
          />
          <Field label="ESI number" value={accounts.esiNumber} onChange={(v) => patchSection('accounts', { esiNumber: v })} />
          {/* The evidence for the account number above — payroll pays against this, not against
              a typed digit string, so a wrong keystroke here is somebody's salary going astray. */}
          <DocumentUploads
            scope={{ token }}
            section="accounts"
            label="Bank documents"
            hint="A cancelled cheque or the first page of your passbook, showing the account number and IFSC."
            keys={accounts.documentKeys}
            onChange={(keys) => patchSection('accounts', { documentKeys: keys })}
            disabled={saving}
          />
        </Section>

        <footer className="cp-actions">
          {showIssues && issues.length > 0 && (
            <span className="cp-actions__count">
              {issues.length} item{issues.length === 1 ? '' : 's'} still need attention
            </span>
          )}
          <button type="button" className="cp-btn" onClick={saveDraft} disabled={saving}>
            Save and finish later
          </button>
          <button type="button" className="cp-btn cp-btn--primary" onClick={submit} disabled={saving}>
            {saving ? 'Submitting…' : 'Submit'}
          </button>
        </footer>
      </div>
    </div>
  );
};

/**
 * A section whose rows the employee adds and removes.
 *
 * Generic over the row type so qualifications, employment, family and nominees share one set of
 * add/remove/keying behaviours — four hand-written copies of "spread the array, replace index i"
 * is where off-by-one row bugs come from.
 */
function RepeatingSection<T extends { id: string }>({
  title,
  required,
  hint,
  issues,
  rows,
  addLabel,
  onAdd,
  onRemove,
  onChange,
  renderRow,
}: {
  title: string;
  /** Marks the title with a *. The rule itself lives in validateEmployeeProfile. */
  required?: boolean;
  hint?: string;
  issues?: string[];
  rows: T[];
  addLabel: string;
  onAdd: () => void;
  onRemove: (id: string) => void;
  /** Replaces the whole array. The parent owns it; this component never mutates in place. */
  onChange: (rows: T[]) => void;
  renderRow: (row: T, update: (partial: Partial<T>) => void) => React.ReactNode;
}) {
  return (
    <section className="cp-section">
      <h2 className="cp-section__title">
        {title}
        {required && <span className="cp-field__req"> *</span>}
      </h2>
      {hint && <p className="cp-section__hint">{hint}</p>}
      {issues && issues.length > 0 && (
        <ul className="cp-issues">
          {issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      )}
      {rows.length === 0 && <p className="cp-empty">Nothing added yet.</p>}
      {rows.map((row, index) => (
        <div className="cp-row" key={row.id}>
          <div className="cp-row__head">
            <span>#{index + 1}</span>
            <button type="button" className="cp-remove" onClick={() => onRemove(row.id)}>
              Remove
            </button>
          </div>
          <div className="cp-grid">
            {renderRow(row, (partial) =>
              /*
               * Matched by ID, never by index.
               *
               * Index-based replacement is where repeating-row bugs live: remove row 2 while row
               * 3 has focus and the next keystroke rewrites a different record than the one on
               * screen. The id is stable for the row's whole life, so it cannot happen.
               */
              onChange(rows.map((r) => (r.id === row.id ? { ...r, ...partial } : r)))
            )}
          </div>
        </div>
      ))}
      <button type="button" className="cp-btn cp-btn--ghost" onClick={onAdd}>
        {addLabel}
      </button>
    </section>
  );
}

export default CompleteProfilePage;
